import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Task, CATEGORY_COLORS } from '../types';
import CountdownTimer from './CountdownTimer';
import { useAuth } from '../contexts/AuthContext';

interface TaskCardProps {
  task: Task;
  onStatusChange: (id: string, status: Task['status'], progress: number) => void;
}

const priorityDot: Record<string, string> = {
  High: '#ef4444',
  Medium: '#f59e0b',
  Low: '#10b981',
};

export default function TaskCard({ task, onStatusChange }: TaskCardProps) {
  const { role, user } = useAuth();
  const [showSlider, setShowSlider] = useState(false);
  const [sliderVal, setSliderVal] = useState(task.progress);
  const canAct = role === 'admin' || user?.id === task.assigned_developer_id;

  const handleComplete = (e: React.MouseEvent) => {
    e.preventDefault();
    onStatusChange(task.id, 'Completed', 100);
  };

  const handleInProgress = (e: React.MouseEvent) => {
    e.preventDefault();
    onStatusChange(task.id, 'In Progress', task.progress > 0 ? task.progress : 10);
  };

  const handleSliderCommit = (e: React.MouseEvent) => {
    e.preventDefault();
    onStatusChange(task.id, task.status === 'Pending' ? 'In Progress' : task.status, sliderVal);
    setShowSlider(false);
  };

  const catColor = CATEGORY_COLORS[task.category] ?? '#6b7280';
  const isCompleted = task.status === 'Completed';

  return (
    <div className={`task-card ${isCompleted ? 'task-card-done' : ''} ${task.carried_over ? 'task-card-carried' : ''}`}
      style={{ borderLeftColor: priorityDot[task.priority] }}>

      {/* Header */}
      <div className="task-card-header">
        <div className="task-card-meta-top">
          <span className="category-badge" style={{ background: catColor + '18', color: catColor, borderColor: catColor + '40' }}>
            {task.category}
          </span>
          {task.carried_over && (
            <span className="carried-badge" title={`Originally from ${task.original_date ?? 'a previous day'}`}>
              ⏳ Carried over
            </span>
          )}
          {task.recurrence !== 'none' && (
            <span className="recurrence-badge">🔁 {task.recurrence}</span>
          )}
        </div>
        <span className={`task-priority-dot priority-dot-${task.priority.toLowerCase()}`}
          title={`${task.priority} priority`} />
      </div>

      {/* Title */}
      <Link to={`/tasks/${task.id}`} className={`task-card-title ${isCompleted ? 'task-title-done' : ''}`}>
        {task.title}
      </Link>

      {/* Description */}
      {task.description && (
        <p className="task-card-description">
          {task.description.length > 90 ? task.description.slice(0, 90) + '…' : task.description}
        </p>
      )}

      {/* Progress bar */}
      <div className="task-progress-container">
        <div className="task-progress-track">
          <div
            className={`task-progress-fill ${isCompleted ? 'progress-complete' : task.status === 'In Progress' ? 'progress-active' : ''}`}
            style={{ width: `${task.progress}%` }}
          />
        </div>
        <button
          className="task-progress-label"
          onClick={(e) => { e.preventDefault(); setShowSlider(!showSlider); }}
          title="Click to update progress"
          type="button"
        >
          {task.progress}%
        </button>
      </div>

      {/* Inline progress slider */}
      {showSlider && !isCompleted && canAct && (
        <div className="progress-slider-row" onClick={e => e.preventDefault()}>
          <input
            type="range" min={0} max={100} step={5}
            value={sliderVal}
            onChange={e => setSliderVal(Number(e.target.value))}
            className="progress-slider"
          />
          <span className="slider-value">{sliderVal}%</span>
          <button className="btn btn-primary btn-sm" onClick={handleSliderCommit}>Set</button>
          <button className="btn btn-secondary btn-sm" onClick={(e) => { e.preventDefault(); setShowSlider(false); }}>✕</button>
        </div>
      )}

      {/* Deadline */}
      {task.deadline && (
        <div className="task-deadline-row">
          <CountdownTimer deadline={task.deadline} compact />
        </div>
      )}

      {/* Actions */}
      {!isCompleted && canAct && (
        <div className="task-card-actions">
          {task.status === 'Pending' && (
            <button className="task-action-btn action-progress" onClick={handleInProgress}>
              ▶ Start
            </button>
          )}
          {task.status === 'In Progress' && (
            <button className="task-action-btn action-complete" onClick={handleComplete}>
              ✓ Complete
            </button>
          )}
          {task.status === 'Pending' && (
            <button className="task-action-btn action-complete" onClick={handleComplete}>
              ✓ Done
            </button>
          )}
          <Link to={`/tasks/${task.id}`} className="task-action-btn action-edit" onClick={e => e.stopPropagation()}>
            ✎ Edit
          </Link>
        </div>
      )}

      {isCompleted && (
        <div className="task-completed-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Completed{task.completed_at ? ` at ${new Date(task.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
        </div>
      )}
    </div>
  );
}
